import { fromUnixTime, getUnixTime } from 'date-fns';
import { camelcaseKeys } from 'src/utils/camelize-keys';
import { formatYearMonthDay } from 'src/utils/dateFormatter';
import { decamelizeKeys } from 'src/utils/decamelize-keys';
import { processDateFilter } from 'src/utils/fieldNormalizers';
import { titleize } from 'src/utils/stringFormatter';

import { HealthReportWidget, Report, ReportConfig } from './_types';

export function normalizeReport(report: any): Report {
  let filters = report.filters || [];

  if (typeof filters === 'string') {
    filters = filters
      .split(',')
      .map((filter) => ({ id: filter, label: titleize(filter) }));
  }

  const { cached_stats, ...rest } = report;

  return {
    ...camelcaseKeys(rest),
    cachedStats: cached_stats,
    createdAt: fromUnixTime(report.created_at),
    updatedAt: fromUnixTime(report.updated_at),
    filters: filters || [],
  } as Report;
}

export function denormalizeReport(report: Report): any {
  return {
    ...report,
    created_at: getUnixTime(report.createdAt),
    updated_at: getUnixTime(report.updatedAt),
  };
}

export function normalizeReportConfig(reportConfig: any): ReportConfig {
  return camelcaseKeys(reportConfig, { deep: true });
}

export function denormalizeParams(params: any): any {
  params =
    params instanceof URLSearchParams ? Object.fromEntries(params) : params;

  if (params.f_serviceTag) {
    try {
      params.service_tag = decodeURIComponent(params.f_serviceTag)
        .replace(/[[\]"]/g, '')
        .split(',')
        .join(',');

      delete params.f_serviceTag;
    } catch (e) {
      // do nothing
    }
  }

  if (params.f_sites) {
    try {
      params.sites = decodeURIComponent(params.f_sites)
        .replace(/[[\]"]/g, '')
        .split(',');
      delete params.f_sites;
    } catch (e) {
      // do nothing
    }
  }

  if (params.f_productTag) {
    try {
      params.product_tag = decodeURIComponent(params.f_productTag)
        .replace(/[[\]"]/g, '')
        .split(',')
        .join(',');

      delete params.f_productTag;
    } catch (e) {
      // do nothing
    }
  }

  // Handle date params
  if (params.f_startDate) {
    params.start_date = getUnixTime(
      new Date(decodeURIComponent(params.f_startDate))
    );
    params.start_date_string = formatYearMonthDay(params.f_startDate);
    delete params.f_startDate;
  }
  if (params.f_endDate) {
    params.end_date = getUnixTime(
      new Date(decodeURIComponent(params.f_endDate))
    );
    params.end_date_string = formatYearMonthDay(params.f_endDate);
    delete params.f_endDate;
  }

  // Process main date filter
  processDateFilter(params, 'date');

  // Process secondary date filter (e.g., charge dates for refunds report)
  processDateFilter(params, 'secondaryDate', 'secondary');

  const cleanParams = {};
  Object.keys(params).forEach((key) => {
    let value = params[key];
    if (key.startsWith('f_')) {
      const cleanKey = key.replace('f_', '');
      // Try to parse encoded JSON values
      if (typeof value === 'string' && value.startsWith('%7B')) {
        try {
          value = JSON.parse(decodeURIComponent(value));
          if (value && cleanKey == 'miaClientTags') {
            value.values = value.values.join(',');
          }
        } catch (e) {
          // If parsing fails, use original value
          value = params[key];
        }
      }
      cleanParams[cleanKey] = value;
    } else {
      cleanParams[key] = value;
    }
  });

  return decamelizeKeys(cleanParams, { deep: true });
}

export function denormalizeReportConfig(reportConfig: ReportConfig): any {
  return {
    column_config: decamelizeKeys(reportConfig.columns, { deep: true }),
  };
}

export function normalizeReportWidget(reportWidget: any): HealthReportWidget {
  return camelcaseKeys(reportWidget, { deep: true });
}

export function denormalizeReportWidget(reportWidget: any): HealthReportWidget {
  return decamelizeKeys(reportWidget, { deep: true });
}
